<script lang="ts" setup>
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/vue/24/outline';
import { router } from '@inertiajs/vue3';
import type { Marker } from '@vuepic/vue-datepicker';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import { useColorMode } from '@vueuse/core';
import { addMinutes, addSeconds, format, isBefore, parseISO } from 'date-fns';
import { useForm } from 'laravel-precognition-vue-inertia';
import { computed, markRaw, onMounted, onUnmounted, ref, watchEffect } from 'vue';
import type { ScheduledSends } from '../Pages/SendShow.vue';

const props = withDefaults(defineProps<{
    /** The PATCH endpoint that schedules the send. */
    url: string;
    /** The send being scheduled, so its own time doesn't count as a clash. */
    sendId: number;
    description: string;
    scheduled?: ScheduledSends;
    /** How far ahead of now the earliest valid time is. */
    minimumLeadMinutes?: number;
}>(), {
    scheduled: undefined,
    minimumLeadMinutes: 5,
});

const scheduledMarkers = ref<Marker[]>([]);

watchEffect(() => {
    if(!props.scheduled) {
        return;
    }

    // `markRaw` keeps each marker a plain (non-reactive) object. VueDatePicker
    // runs `structuredClone()` on the day cell when it is selected, and a Vue
    // reactive proxy cannot be structured-cloned — which silently aborted
    // selecting any day that already had a scheduled send (ALC-431).
    scheduledMarkers.value = props.scheduled?.markers.map(marker => markRaw({
        ...marker,
        // The backend sends a localized `Y-m-d` string; `parseISO` reads it as
        // local midnight, whereas `new Date('Y-m-d')` would treat it as UTC and
        // render the dot a day early in any negative-UTC timezone.
        date: typeof marker.date === 'string' ? parseISO(marker.date) : marker.date,
        color: '#f43f5e',
        tooltip: marker.tooltip?.map((tooltip: any) => {
            return {
                ...tooltip,
                color: '#f43f5e'
            };
        })
    }));

});

const mode = useColorMode();

const defaultDateTime = addMinutes(new Date(), 10);

const form = useForm('patch', props.url, {
    send_at: defaultDateTime.toISOString(),
});

const earliestSendAt = ref(addMinutes(new Date(), props.minimumLeadMinutes));

const interval = setInterval(() => {
    earliestSendAt.value = addSeconds(new Date(earliestSendAt.value), 1);
}, 1000);

const isTimeValid = computed(() => {
    const now = new Date(new Date(form.send_at).toLocaleString());

    if(!props.scheduled) {
        return true;
    }

    for(const send of props.scheduled.sends) {
        if(send.id === props.sendId) {
            return true;
        }

        if(!send.scheduled_block?.start || !send.scheduled_block?.end) {
            continue;
        }

        const start = new Date(new Date(send.scheduled_block.start).toLocaleString());
        const end = new Date(new Date(send.scheduled_block.end).toLocaleString());
        
        if(start <= now && end >= now) {
            return false;
        }
    }

    if(isBefore(now, earliestSendAt.value)) {
        return false;
    }

    return true;
});

onMounted(() => {
    router.reload({
        only: ['scheduled']
    });
});

onUnmounted(() => {
    clearInterval(interval);
});

defineExpose({
    form,
    url: props.url
});
</script>

<template>
    <div
        class="flex flex-col gap-4">
        <h1>
            {{ description }}
        </h1>

        <VueDatePicker
            v-model="form.send_at"
            inline
            :markers="scheduledMarkers"
            :is-24="false"
            auto-apply
            :month-change-on-scroll="false"
            :dark="mode === 'dark'" />

        <div
            v-if="form.errors.send_at"
            class="text-red-500">
            {{ form.errors.send_at }}
        </div>

        <div
            class="p-2 rounded flex gap-x-2 justify-start items-center text-lg text-white"
            :class="{
                'bg-emerald-500': isTimeValid,
                'bg-red-500': !isTimeValid
            }">
            <template v-if="isTimeValid">
                <CheckCircleIcon
                    class="h-8 w-8 shrink-0" />
                {{ format(new Date(form.send_at), 'PPPp') }}
            </template>
            <template v-else>
                <ExclamationCircleIcon
                    class="w-8 h-8 shrink-0" />
                {{ format(new Date(form.send_at), 'PPPp') }}
            </template>
        </div>
    </div>
</template>

<style>
.dp__flex_display {
    display: block !important;
}

.dp__menu {
    border: none !important;
}

.dp__menu {
    border: none;
}

.dp__menu {
    border: none;
}
</style>